import { useEffect, useRef, useState } from 'react';
import { Seo } from '@/components/Seo';
import { ContentPage, P } from '@/components/PublicLayout';
import { useLang, useT } from '@/i18n/LanguageContext';
import { bioContent, cvItems, localizeCvItem } from '@/i18n/content/bio';
import { pathFor } from '@/i18n/routes';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const PORTRAIT =
  'https://cdn.myportfolio.com/aed5c2ea91a08c53738335405d8ea85c/aaad9e3b-a719-4bf8-a3dc-3e7b9dbd2459_rw_3840.jpg?h=d163956d3a3b650caae42dad99c0b013';

// Bron van het archief (Adobe Portfolio).
const ARCHIVE_URL = 'https://rot8ionold.myportfolio.com/archief';
// Hoogte van de header (logo + menu + social icons) op de bronpagina; wordt weggesneden.
const HEADER_CROP = 130;
// Terugvalhoogte zolang het aantal projecten nog niet bekend is of niet op te halen valt.
const FALLBACK_HEIGHT = 16000;
// Hoogte voor een losse projectpagina in het venster.
const PROJECT_HEIGHT = 6000;
// Gemeten kenmerken van de Adobe Portfolio-grid: tegelhoogte ≈ 0,8 × tegelbreedte.
const TILE_RATIO = 0.8;
// Ruimte boven de eerste tegel (titelblok) en onder de laatste tegel (footer).
const GRID_TOP = 130;
const GRID_BOTTOM = 160;
// Onder deze breedte toont Portfolio één kolom in plaats van twee.
const TWO_COLUMN_MIN_WIDTH = 640;

function gridHeight(projects: number, width: number) {
  const cols = width >= TWO_COLUMN_MIN_WIDTH ? 2 : 1;
  const tileHeight = (width / cols) * TILE_RATIO;
  return Math.round(GRID_TOP + Math.ceil(projects / cols) * tileHeight + GRID_BOTTOM);
}

export default function Bio() {
  const lang = useLang();
  const t = useT(bioContent);
  const [params, setParams] = useSearchParams();
  const [frameKey, setFrameKey] = useState(0);
  const archiveRef = useRef<HTMLDivElement>(null);
  const frameBoxRef = useRef<HTMLDivElement>(null);
  const [projects, setProjects] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const project = params.get('project');
  // Alleen archief-URL's toestaan om open redirects/embeds te voorkomen.
  const archiveSrc =
    project && project.startsWith('https://rot8ionold.myportfolio.com/') ? project : ARCHIVE_URL;

  useEffect(() => {
    if (project && archiveRef.current) {
      archiveRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [project]);

  // Aantal projecten live ophalen; het venster wordt daarop afgestemd.
  useEffect(() => {
    let cancelled = false;
    supabase.functions
      .invoke('archive-metrics')
      .then(({ data }) => {
        const n = (data as { projects?: number | null } | null)?.projects;
        if (!cancelled && typeof n === 'number' && n > 0) setProjects(n);
      })
      .catch(() => {
        /* stil: we vallen terug op de standaardhoogte */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Kolomaantal hangt af van de breedte van het venster.
  useEffect(() => {
    const el = frameBoxRef.current;
    if (!el) return;
    const update = () => setBoxWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const frameHeight =
    (project
      ? PROJECT_HEIGHT
      : projects && boxWidth
        ? gridHeight(projects, boxWidth)
        : FALLBACK_HEIGHT) + HEADER_CROP;


  return (
    <>
      <Seo title={t.seoTitle} description={t.seoDescription} path={pathFor('bio', lang)} />
      <ContentPage title={t.title}>
        <div className="mb-5 leading-relaxed">
          <p className="text-sitefg/60">{t.intro}</p>
          {t.introDetails.map((line, i) => (
            <p key={i} className="text-sitefg/75">{line}</p>
          ))}
        </div>
        {t.paragraphs.map((p, i) => (
          <P key={i}>{p}</P>
        ))}
        <img
          src={PORTRAIT}
          alt={t.portraitAlt}
          loading="lazy"
          className="w-full h-auto mt-8 rounded-sm"
        />

        <div id="archive" ref={archiveRef} className="scroll-mt-24">
          <h2 className="text-2xl md:text-3xl font-light tracking-tight mt-16 mb-6">
            {t.archiveTitle}
          </h2>
          {/* Altijd zichtbaar: het iframe is cross-origin, dus we kunnen navigatie erin niet volgen.
              De knop herlaadt het archiefoverzicht (key-bump forceert een verse iframe). */}
          <button
            type="button"
            onClick={() => {
              setFrameKey((k) => k + 1);
              if (project) setParams({}, { replace: true });
            }}
            className="mb-4 inline-block px-5 py-2 rounded-sm bg-[var(--viz-accent)] text-white text-sm tracking-wide hover:bg-[var(--viz-6)] transition-colors"
          >
            {t.archiveBack}
          </button>
          {/* Binnen de tekstkolom. De buitenste div scrollt (eigen, zwarte scrollbalk);
              het iframe zelf is extra hoog en scrolt niet. */}
          <div
            ref={frameBoxRef}
            className="archive-scroll relative w-full h-[85vh] min-h-[700px] overflow-y-scroll overflow-x-hidden border-y border-black/10 bg-white ring-1 ring-black/10"
          >
            <iframe
              key={frameKey}
              src={archiveSrc}
              title={t.archiveFrameTitle}
              loading="lazy"
              scrolling="no"
              className="block w-full border-0 bg-white"
              style={{
                marginTop: -HEADER_CROP,
                height: frameHeight,
              }}
            />
          </div>
        </div>

        <h2 className="text-2xl md:text-3xl font-light tracking-tight mt-16 mb-6">
          {t.cvTitle}
        </h2>
        <ul>
          {cvItems.map((item, i) => (
            <li key={`${i}-${item}`} className="text-sm leading-relaxed text-sitefg/75">
              {localizeCvItem(item, lang)}
            </li>
          ))}
        </ul>
      </ContentPage>
    </>
  );
}
